import React, { useState, useEffect } from 'react';
import './SetupScreen.css';
import MediaManagement from './MediaManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function SetupScreen({ onStart, onLock }) {
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [videos, setVideos] = useState([]);
  const [audios, setAudios] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState('');
  const [selectedAudios, setSelectedAudios] = useState([]);
  const [error, setError] = useState('');
  const [showManagement, setShowManagement] = useState(false);

  useEffect(() => {
    loadMedia();
  }, []);

  const loadMedia = async () => {
    try {
      const response = await fetch(`${API_URL}/api/media/list`);
      const data = await response.json();
      setVideos(data.videos || []);
      
      // Load audio files with their durations
      const audioFiles = data.audios || [];
      const audiosWithDuration = await Promise.all(
        audioFiles.map(audio => {
          return new Promise((resolve) => {
            const audioElement = new Audio();
            audioElement.src = `${API_URL}/api/static/audio/${audio.name}`;
            
            audioElement.onloadedmetadata = () => {
              resolve({
                ...audio,
                duration: audioElement.duration
              });
            };
            
            audioElement.onerror = () => {
              // If loading fails, return without duration
              resolve({
                ...audio,
                duration: 0
              });
            };
          });
        })
      );
      
      setAudios(audiosWithDuration);
      
      // Auto-select first video if available and none is selected
      if (data.videos && data.videos.length > 0) {
        setSelectedVideo(prev => prev || data.videos[0].name);
      }
    } catch (err) {
      setError('Fehler beim Laden der Medien');
    }
  };

  const handleStart = () => {
    const totalSeconds = parseInt(minutes || 0) * 60 + parseInt(seconds || 0);
    
    if (totalSeconds < 1) {
      setError('Bitte geben Sie mindestens 1 Sekunde ein');
      return;
    }
    
    if (!selectedVideo) {
      setError('Bitte wählen Sie ein Video aus');
      return;
    }
    
    if (selectedAudios.length === 0) {
      setError('Bitte wählen Sie mindestens ein Lied aus');
      return;
    }
    
    setError('');
    onStart({ 
      totalSeconds,
      video: selectedVideo,
      audios: selectedAudios
    });
  };

  const handleMinutesChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 999)) {
      setMinutes(value);
      setError('');
    }
  };
  
  const handleSecondsChange = (e) => {
    const value = e.target.value;
    if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 59)) {
      setSeconds(value);
      setError('');
    }
  };
  
  const toggleAudioSelection = (audioName) => {
    setSelectedAudios(prev => {
      if (prev.includes(audioName)) {
        return prev.filter(a => a !== audioName);
      } else {
        return [...prev, audioName];
      }
    });
    setError('');
  };

  // Calculate total audio duration
  const getTotalDuration = () => {
    const selectedAudioObjects = audios.filter(a => selectedAudios.includes(a.name));
    const total = selectedAudioObjects.reduce((sum, audio) => sum + (audio.duration || 0), 0);
    return total;
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="setup-screen" data-testid="setup-screen">
      <button
        className="lock-button"
        onClick={onLock}
        data-testid="lock-button"
        aria-label="Sperren"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Sperren
      </button>

      <div className="setup-container fade-in">
        <div className="setup-header">
          <h1 className="setup-title">Timer einrichten</h1>
          <p className="setup-subtitle">Wählen Sie Thema und Dauer</p>
        </div>

        <div className="time-input-section">
          <h3>Timer-Dauer</h3>
          <div className="time-inputs">
            <div className="time-input-group">
              <label htmlFor="minutes" className="time-label">Minuten</label>
              <input
                id="minutes"
                type="number"
                min="0"
                max="999"
                value={minutes}
                onChange={handleMinutesChange}
                className={`time-input ${error ? 'error' : ''}`}
                data-testid="minutes-input"
              />
            </div>
            <div className="time-separator">:</div>
            <div className="time-input-group">
              <label htmlFor="seconds" className="time-label">Sekunden</label>
              <input
                id="seconds"
                type="number"
                min="0"
                max="59"
                value={seconds}
                onChange={handleSecondsChange}
                className={`time-input ${error ? 'error' : ''}`}
                data-testid="seconds-input"
              />
            </div>
          </div>
        </div>

        <div className="media-section">
          <div className="section-header">
            <h3>Video auswählen</h3>
            <button 
              className="manage-button"
              onClick={() => setShowManagement(true)}
              data-testid="manage-media-button"
            >
              Medien verwalten
            </button>
          </div>
          <div className="video-selection">
            {videos.map(video => (
              <button
                key={video.name}
                className={`media-item ${selectedVideo === video.name ? 'selected' : ''}`}
                onClick={() => setSelectedVideo(video.name)}
                data-testid={`video-${video.name}`}
              >
                <div className="media-icon">🎥</div>
                <span className="media-name">{video.name}</span>
              </button>
            ))}
            {videos.length === 0 && (
              <p className="no-media">Keine Videos verfügbar</p>
            )}
          </div>
        </div>

        <div className="media-section">
          <h3>Musik auswählen (mehrere möglich)</h3>
          <div className="audio-selection">
            {audios.map(audio => (
              <button
                key={audio.name}
                className={`media-item ${selectedAudios.includes(audio.name) ? 'selected' : ''}`}
                onClick={() => toggleAudioSelection(audio.name)}
                data-testid={`audio-${audio.name}`}
              >
                <div className="media-icon">🎵</div>
                <div className="media-name">{audio.name}</div>
                {audio.duration && (
                  <div className="media-duration">{formatDuration(audio.duration)}</div>
                )}
                {selectedAudios.includes(audio.name) && (
                  <div className="selected-indicator">✓</div>
                )}
              </button>
            ))}
            {audios.length === 0 && (
              <p className="no-media">Keine Musik verfügbar</p>
            )}
          </div>
        </div>

        {selectedAudios.length > 0 && getTotalDuration() > 0 && (
          <div className="music-duration-info">
            🎵 Musik-Gesamtdauer: <strong>{formatDuration(getTotalDuration())}</strong>
          </div>
        )}

        {error && (
          <div className="error-message" data-testid="error-message">
            {error}
          </div>
        )}

        <button
          className="start-button"
          onClick={handleStart}
          data-testid="start-button"
        >
          Timer starten
        </button>
      </div>

      {showManagement && (
        <MediaManagement 
          onClose={() => setShowManagement(false)}
          onMediaUpdated={loadMedia}
        />
      )}
    </div>
  );
}

export default SetupScreen;